import type { Track } from "livekit-client";
import type { Member, Tile } from "./huddle";
import type { UUID } from "./sync/types";

// How a call's people and screens are laid out on the stage, as video apps
// do. Auto picks by what goes on: two people are the other filling the stage
// and oneself in a corner; more, an equal grid; a screen shared, the screen
// large with people in a strip. Speaker puts whoever is talking large;
// Gallery is always the grid. A box pinned is large whatever the layout.

export type Layout = "auto" | "speaker" | "gallery";

// Box is one on the stage: a person, with their camera if it is on, or a
// screen someone shares.
export interface Box {
  key: string;
  who: UUID;
  me: boolean;
  screen: boolean;
  track?: Track;
  micOff?: boolean;
}

export interface View {
  layout: Layout;
  // sideBySide shows two people as equal boxes in Auto, not one in a corner.
  sideBySide: boolean;
  hideSelf: boolean;
  hideNoVideo: boolean;
}

export const defaultView: View = { layout: "auto", sideBySide: false, hideSelf: false, hideNoVideo: false };

export type Arrangement = { kind: "grid"; boxes: Box[] } | { kind: "focus"; main: Box; strip: Box[] } | { kind: "pip"; main: Box; pip: Box };

// boxesOf is the call's boxes: screens first, then everyone in the order
// they joined, with their camera.
export function boxesOf(members: Member[], tiles: Tile[]): Box[] {
  const screens = tiles.filter((t) => t.screen).map((t) => ({ key: t.key, who: t.who, me: members.some((m) => m.me && m.id === t.who), screen: true, track: t.track }));
  const people = members.map((m) => ({
    key: m.id,
    who: m.id,
    me: m.me,
    screen: false,
    track: tiles.find((t) => !t.screen && t.who === m.id)?.track,
    micOff: m.micOff,
  }));
  return [...screens, ...people];
}

// arrange lays the boxes out for a view, with the box pinned, if any, and
// whoever is talking.
export function arrange(all: Box[], view: View, pinned: string | null, speaker: UUID | null): Arrangement {
  const boxes = all.filter((b) => b.screen || ((!view.hideSelf || !b.me) && (!view.hideNoVideo || b.track)));
  const grid: Arrangement = { kind: "grid", boxes };
  const focus = (main: Box): Arrangement => (boxes.length > 1 ? { kind: "focus", main, strip: boxes.filter((b) => b !== main) } : grid);
  const pin = pinned ? boxes.find((b) => b.key === pinned) : undefined;
  if (pin) return focus(pin);
  if (view.layout === "gallery" || boxes.length === 0) return grid;
  const screen = boxes.find((b) => b.screen);
  if (screen) return focus(screen);
  const others = boxes.filter((b) => !b.me);
  if (view.layout === "speaker") return focus(others.find((b) => b.who === speaker) ?? others[0] ?? boxes[0]);
  const self = boxes.find((b) => b.me);
  if (boxes.length === 2 && self && others.length === 1 && !view.sideBySide) return { kind: "pip", main: others[0], pip: self };
  return grid;
}

// fit is the grid that makes n boxes of an aspect as large as they can be
// in a width and height: its columns, and each box's width.
export function fit(n: number, width: number, height: number, aspect = 16 / 9, gap = 6): { columns: number; width: number } {
  let best = { columns: 1, width: 0 };
  for (let columns = 1; columns <= Math.max(1, n); columns++) {
    const rows = Math.ceil(n / columns);
    let w = (width - (columns - 1) * gap) / columns;
    if ((w / aspect) * rows + (rows - 1) * gap > height) w = ((height - (rows - 1) * gap) / rows) * aspect;
    if (w > best.width) best = { columns, width: Math.floor(w) };
  }
  return best;
}

// page is one page of a grid's boxes, and how many pages there are.
export function page<T>(boxes: T[], size: number, at: number): { boxes: T[]; at: number; pages: number } {
  const pages = Math.max(1, Math.ceil(boxes.length / size));
  const i = Math.min(Math.max(0, at), pages - 1);
  return { boxes: boxes.slice(i * size, (i + 1) * size), at: i, pages };
}
